import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Login mit Benutzername oder E-Mail
  const login = async (loginInput, password) => {
    const response = await axios.post(`${API}/auth/login`, { 
      login: loginInput, 
      password 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return { token: newToken, user: userData };
  };

  // Erzwungene Passwortänderung nach Admin-Reset
  const forcePasswordChange = async (newPassword) => {
    const response = await axios.post(`${API}/auth/force-password-change`, {
      new_password: newPassword
    });
    // Aktualisiere User-Daten
    await fetchUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isEmployee = () => user?.role === 'mitarbeiter';
  const needsPasswordChange = () => user?.password_reset_required === true;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      forcePasswordChange,
      isAdmin, 
      isEmployee,
      needsPasswordChange 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
